import { useRef, useState } from 'react';
import { useAddStatementLine, useIgnoreLine, useImportStatement, useMatchPayment, useStatement, useUnmatchLine } from '@/api/studio';
import type { Invoice, StatementBasis, StatementLine, StatementPick } from '@/api/types';
import { useStudioTerms, withSign } from '@/lib/contour';
import { formatDate, formatMoney } from '@/lib/format';

const basisLabel: Record<StatementBasis, string> = { number: 'номер в назначении', amount: 'та же сумма', payer: 'тот же плательщик' };

type Pick = { invoiceId: string; amount: string };

const toPicks = (picks: Pick[]): StatementPick[] =>
  picks
    .filter((p) => p.invoiceId)
    .map((p) => {
      const minor = Math.round(Number(p.amount.replace(',', '.')) * 100);
      return p.amount.trim() && minor > 0 ? { invoiceId: p.invoiceId, amountMinor: minor } : { invoiceId: p.invoiceId };
    });

/**
 * Bank statement lines the studio matches to open invoices (RU contour, owners). One line can pay several
 * invoices; what stays over is shown as «лишнее». A match can be undone, a line that is not ours hidden.
 */
export function Statement({ invoices }: { invoices: Invoice[] }) {
  const { contour, acts } = useStudioTerms();
  const bank = contour?.payments.provider === 'tochka' ? 'Точки' : 'банка';
  const { data } = useStatement();
  const [all, setAll] = useState(false);
  const open = invoices.filter((i) => i.status === 'sent' || i.status === 'overdue');
  const pending = data?.filter((l) => l.status === 'unmatched' || l.status === 'partial') ?? [];
  const shown = all ? (data ?? []) : pending;

  return (
    <section className="mb-10 rounded-lg p-5 shadow-hair" aria-label="Выписка банка">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Выписка банка: {data ? (pending.length ? `не сопоставлено ${pending.length}` : 'всё сопоставлено') : 'загружается'}</h2>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Показать все строки
        </label>
      </div>
      <p className="mt-1 text-sm text-muted">Поступления из выписки {bank}. Сопоставьте со счётом — он станет оплаченным{acts ? ', клиент получит акт' : ''}. Одну строку можно разнести на несколько счетов.</p>
      <ul className="mt-3 divide-y divide-line">
        {shown.map((l) => (
          <Line key={l.id} line={l} open={open} />
        ))}
      </ul>
      <ImportCsv />
      <AddLine />
    </section>
  );
}

function Line({ line: l, open }: { line: StatementLine; open: Invoice[] }) {
  const match = useMatchPayment();
  const unmatch = useUnmatchLine();
  const ignore = useIgnoreLine();
  const [picks, setPicks] = useState<Pick[] | null>(null);
  const current: Pick[] = picks ?? [{ invoiceId: l.suggestions[0]?.invoiceId ?? '', amount: '' }];
  const patch = (i: number, p: Partial<Pick>) => setPicks(current.map((x, n) => (n === i ? { ...x, ...p } : x)));
  const head = (
    <>
      <span className="num w-28 font-medium">{formatMoney(l.amount)}</span>
      <span className="min-w-0 flex-1">
        {l.payer}
        <span className="block truncate text-xs text-muted">
          {formatDate(l.date)}, «{l.purpose}»
        </span>
      </span>
    </>
  );

  if (l.status === 'matched') {
    return (
      <li className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
        {head}
        <span className="text-xs text-muted">
          {l.allocations.length ? l.allocations.map((a) => `№ ${a.number}: ${formatMoney({ amountMinor: a.amountMinor, currency: l.amount.currency })}`).join('; ') : 'сопоставлена'}
        </span>
        {l.overpaid && l.overpaid.amountMinor > 0 && <span className="text-xs text-waiting">лишнее {formatMoney(l.overpaid)}</span>}
        <button className="btn-ghost h-8" disabled={unmatch.isPending} onClick={() => unmatch.mutate(l.id)}>
          Отменить сопоставление
        </button>
      </li>
    );
  }
  if (l.status === 'ignored') {
    return (
      <li className="flex flex-wrap items-center gap-3 py-2.5 text-sm text-muted">
        {head}
        <span className="text-xs">скрыта</span>
        <button className="btn-ghost h-8" disabled={unmatch.isPending} onClick={() => unmatch.mutate(l.id)}>
          Вернуть
        </button>
      </li>
    );
  }
  const chosen = toPicks(current);
  return (
    <li className="py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        {head}
        {l.status === 'partial' && <span className="text-xs text-waiting">оплачено частично: {formatMoney(l.allocated)}</span>}
      </div>
      {l.suggestions.length > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
          Похоже на:
          {l.suggestions.map((s) => (
            <button key={s.invoiceId} className="btn-ghost h-6 px-2 text-xs" onClick={() => setPicks([{ invoiceId: s.invoiceId, amount: '' }])}>
              № {s.number}, {s.clientName} · {basisLabel[s.basis]}
            </button>
          ))}
        </div>
      )}
      <div className="mt-2 space-y-1.5">
        {current.map((p, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <select className="field h-8 w-60 py-0" aria-label="Счёт" value={p.invoiceId} onChange={(e) => patch(i, { invoiceId: e.target.value })}>
              <option value="">Выберите счёт</option>
              {open.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  № {inv.number}, {inv.clientName ? `${inv.clientName}, ` : ''}
                  {formatMoney(inv.total)}
                </option>
              ))}
            </select>
            <input className="field num h-8 w-36 py-0" aria-label="Сумма по счёту" inputMode="decimal" placeholder="сколько уместится" value={p.amount} onChange={(e) => patch(i, { amount: e.target.value.replace(/[^\d.,]/g, '') })} />
            {current.length > 1 && (
              <button className="btn-ghost h-8 px-2" aria-label="Убрать счёт" onClick={() => setPicks(current.filter((_, n) => n !== i))}>
                ✕
              </button>
            )}
          </div>
        ))}
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary h-8" disabled={!chosen.length || match.isPending} onClick={() => match.mutate({ lineId: l.id, picks: chosen })}>
            Сопоставить
          </button>
          <button className="btn-ghost h-8" onClick={() => setPicks([...current, { invoiceId: '', amount: '' }])}>
            + счёт
          </button>
          {l.status === 'unmatched' && (
            <button className="btn-ghost h-8" disabled={ignore.isPending} onClick={() => ignore.mutate(l.id)}>
              Скрыть: не наше
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

/** A statement exported from the bank's online office: new lines are added, the ones already there are skipped. */
function ImportCsv() {
  const imp = useImportStatement();
  const input = useRef<HTMLInputElement>(null);
  const r = imp.data;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-line pt-4 text-sm">
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        aria-label="Файл выписки CSV"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) imp.mutate(f);
          e.target.value = '';
        }}
      />
      <button className="btn-secondary" disabled={imp.isPending} onClick={() => input.current?.click()}>
        Загрузить выписку (CSV)
      </button>
      <span className="text-xs text-muted">Столбцы: дата, сумма, плательщик, назначение. Строки, которые уже есть, не задваиваются.</span>
      {r && (
        <span role="status" className="text-xs">
          Добавлено {r.added}, уже были {r.duplicates}
          {r.unreadable ? `, не прочитано ${r.unreadable}` : ''}
        </span>
      )}
      {imp.error && (
        <span role="alert" className="text-xs text-overdue">
          {imp.error.message}
        </span>
      )}
    </div>
  );
}

/** A single incoming payment typed from the bank app. */
function AddLine() {
  const { currency, sign } = useStudioTerms();
  const add = useAddStatementLine();
  const [amount, setAmount] = useState('');
  const [payer, setPayer] = useState('');
  const [purpose, setPurpose] = useState('');
  const [on, setOn] = useState(new Date().toISOString().slice(0, 10));
  return (
    <form
      className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[140px_130px_1fr_1fr_auto]"
      onSubmit={(e) => {
        e.preventDefault();
        if (!Number(amount) || !payer.trim()) return;
        add.mutate({ date: on, amount: { amountMinor: Math.round(Number(amount) * 100), currency }, payer: payer.trim(), purpose: purpose.trim() }, { onSuccess: () => (setAmount(''), setPayer(''), setPurpose('')) });
      }}
    >
      <input className="field num" placeholder={withSign('Сумма', sign)} inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} />
      <input className="field" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
      <input className="field" placeholder="Плательщик" value={payer} onChange={(e) => setPayer(e.target.value)} />
      <input className="field" placeholder="Назначение платежа" value={purpose} onChange={(e) => setPurpose(e.target.value)} />
      <button className="btn-secondary" disabled={add.isPending}>
        Добавить поступление
      </button>
    </form>
  );
}
